import type { ReactNode } from 'react';
import React from 'react';
import type { z } from 'zod';

import { Sites, getPublicConfig } from '@sola/config/lib/getters.ts';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { keys } from '@sola/contentful-model/keys.ts';
import type { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';
import i18n from '@sola/locales/index.ts';
import type { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import type {
  ArticleResponseDto,
  ContentResponseBody,
  ContentResponseDto,
  ContributorResponseDto,
  EmbedResponseDto,
  FlexibleContentDto,
  FormConfigResponseDto,
  LongformContentResponseDto,
  Media,
  MergeFieldOptions,
  NavResponseDto,
  PageResponseDto,
  PageResponseIndexDto,
  SacFilterResponseDto,
} from '@sola/schema/lib/contentful.ts';
import type { FundProps, Link, MergeData } from '@sola/schema/lib/schema.ts';
import {
  ArticleProps,
  ContributorProps,
  EmbedProps,
  FlexibleContentProps,
  FormConfigProps,
  MediaProps,
  NavigationProps,
  SacFilterProps,
} from '@sola/schema/lib/schema.ts';
import { slashes } from '@sola/utils/url.ts';

import {
  convertContentResponseToFlexibleContent,
  convertContentResponseToFund,
  convertContentResponseToLink,
  convertMediaResponseToMedia,
} from './response.tsx';
import { getRichText } from './rich-text.tsx';
import { mergeFields } from './utils/mergeFields.ts';

const processBodyMergeFields = (
  elements: ReactNode[],
  fieldsToMerge: MergeFieldOptions,
  mergeFallbackOptions?: MergeFieldOptions,
) => {
  const modifiedElements: ReactNode[] = [];

  for (const element of elements) {
    if (typeof element === 'string' || Array.isArray(element)) {
      const string = Array.isArray(element) ? element[0] : element;
      const mergedString = mergeFields(
        string,
        fieldsToMerge,
        mergeFallbackOptions,
      );
      modifiedElements.push(mergedString);
    } else if (React.isValidElement(element)) {
      let { children } = element.props as { children?: ReactNode | undefined };

      if (Array.isArray(children)) {
        const allAreStrings = children.every(
          (child) => typeof child === 'string',
        );
        children = allAreStrings ? children.join('') : children;
      } else {
        children = [children];
      }

      // things like Bold show as a string so we normalize it into an array
      const childrenArray: ReactNode[] = Array.isArray(children)
        ? children
        : [children];
      const modifiedChildren = processBodyMergeFields(
        childrenArray,
        fieldsToMerge,
        mergeFallbackOptions,
      );
      const updatedProps = {
        ...(element.props as AnyObject),
        children: modifiedChildren,
      };
      // @TODO: Using cloneElement is uncommon and can lead to fragile code.
      modifiedElements.push(React.cloneElement(element, updatedProps));
    } else {
      modifiedElements.push(element);
    }
  }

  return modifiedElements;
};

export const processMergeFields = (
  fields: Pick<
    FlexibleContentProps,
    'title' | 'primaryCTA' | 'secondaryCTA' | 'tertiaryCTA' | 'body' | 'items'
  >,
  mergeData?: MergeData,
) => {
  const { t } = i18n;
  const countryName = mergeData?.pageData?.country;
  const fieldsToMerge: MergeFieldOptions = {
    CHILD_NAME:
      mergeData?.richmixChildData?.name.preferred ??
      mergeData?.childDetails?.name.preferred,
    SOURCE_CODE:
      mergeData?.cookies?.find((c) => c.name === 'referrer')?.value ?? '',
    COUNTRY_NAME: countryName ? t(`geo:countries.${countryName}.nameS`) : '',
    CDSP_RATE: mergeData?.campaignData?.cdspOverrideRate?.toString(),
    UNAVAILABLE_CHILD_NAME:
      mergeData?.childName ?? mergeData?.childDetails?.name.preferred,
  };

  const mergeFallbackOptions: MergeFieldOptions = {
    CHILD_NAME: t('priority:mergeFallbacks.childName'),
    COUNTRY_NAME: t('priority:mergeFallbacks.countryName'),
    UNAVAILABLE_CHILD_NAME: {
      default: t('priority:mergeFallbacks.childName'),
      cap: t('priority:mergeFallbacks.childNameCap'),
    },
  };

  const title =
    fields?.title &&
    mergeFields(fields.title, fieldsToMerge, mergeFallbackOptions);

  const primaryCTA = fields?.primaryCTA && {
    ...fields.primaryCTA,
    title: mergeFields(
      fields.primaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.primaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const secondaryCTA = fields?.secondaryCTA && {
    ...fields.secondaryCTA,
    title: mergeFields(
      fields.secondaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.secondaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const tertiaryCTA = fields?.tertiaryCTA && {
    ...fields.tertiaryCTA,
    title: mergeFields(
      fields.tertiaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.tertiaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const body =
    fields?.body &&
    processBodyMergeFields(
      Array.isArray(fields.body) ? fields.body : [fields.body],
      fieldsToMerge,
      mergeFallbackOptions,
    );

  const items = fields?.items?.map((i) => ({
    ...i,
    title:
      i?.title && mergeFields(i.title, fieldsToMerge, mergeFallbackOptions),
    primaryCTA: i?.primaryCTA && {
      ...i.primaryCTA,
      title: mergeFields(
        i.primaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(i.primaryCTA.link, fieldsToMerge, mergeFallbackOptions),
    },
    secondaryCTA: i?.secondaryCTA && {
      ...i.secondaryCTA,
      title: mergeFields(
        i.secondaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(
        i.secondaryCTA.link,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
    },
    tertiaryCTA: i?.tertiaryCTA && {
      ...i.tertiaryCTA,
      title: mergeFields(
        i.tertiaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(
        i.tertiaryCTA.link,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
    },
    body:
      i?.body &&
      processBodyMergeFields(
        Array.isArray(i.body) ? i.body : [i.body],
        fieldsToMerge,
        mergeFallbackOptions,
      ),
  }));

  return {
    ...fields,
    primaryCTA,
    secondaryCTA,
    tertiaryCTA,
    title,
    body,
    items,
  };
};

// @TODO: Unit Test
export const extractArgsContent = (source: ContentResponseDto) => {
  const convertedBody = getRichText(source.body as ContentResponseBody);
  const convertedSource = convertContentResponseToFlexibleContent(
    source as ContentResponseDto,
  );

  return FlexibleContentProps.parse({
    ...convertedSource,
    body: convertedBody?.content,
  });
};

// @TODO: Unit Test
export const extractArgsLongformContent = (
  source: LongformContentResponseDto,
) => {
  const convertedBody = getRichText(source.body as ContentResponseBody);
  return FlexibleContentProps.parse({
    body: convertedBody?.content,
  });
};

// @TODO: Unit Test
export const extractArgsFormConfig = (source: FormConfigResponseDto) => {
  const relatedContentProps = source.relatedContent
    ? extractArgsContent(source.relatedContent)
    : {};

  return FormConfigProps.parse({
    ...relatedContentProps,
    // config: source.config || undefined,
    config: undefined,
  });
};

// @TODO: Unit Test
export const extractArgsSacFilter = (
  source: SacFilterResponseDto,
  mergeFieldData?: MergeData,
) => {
  const relatedContentProps = source.relatedContent
    ? extractArgsContent(source.relatedContent)
    : {};
  return SacFilterProps.parse({
    limit: source.limit || undefined,
    ageMax: source.ageMax || undefined,
    ageMin: source.ageMin || undefined,
    birthDate: source.birthDate || undefined,
    centerId: source.centerId || undefined,
    countries: source.countries || [],
    gender: source.gender || undefined,
    // For the boolean flags, we consider false and undefined to be the same. We only care about true.
    disability: source.disability || undefined,
    hiv: source.hiv || undefined,
    highlyVulnerable: source.highlyVulnerable || undefined,
    longestWaiting: source.longestWaiting || undefined,
    orphan: source.orphan || undefined,
    ...relatedContentProps,
    pathToSacPage: mergeFieldData?.pathToSacPage,
    pathToChildBio: mergeFieldData?.pathToChildBio,
  });
};

// @TODO: Unit Tests
export const extractArgsNavigation = (
  navItem: NavResponseDto,
): NavigationProps => {
  const convertedLink = convertContentResponseToLink(
    navItem.link,
    navItem.linkedContentCollection,
  );
  const ctaProps = (() => {
    if (navItem.cta?.__typename === 'FlexibleContent') {
      return extractArgsContent(navItem.cta);
    }
    if (navItem.cta?.__typename === 'SacFilter') {
      return extractArgsSacFilter(navItem.cta);
    }
    return;
  })();

  let { badge } = navItem;
  if (navItem.contentPosition?.includes('myNotificationsLink')) {
    // @TODO: Get a real notifications count;
    const unreadNotificationsCount = 3;
    badge = navItem.badge || {
      text: `${unreadNotificationsCount || 0}`,
      color: 'positive',
    };
  }

  return NavigationProps.parse({
    title: navItem.title || convertedLink?.title || '',
    description: navItem.summary || undefined,
    icon: navItem.icon || undefined,
    link: convertedLink?.link,
    target: convertedLink?.target,
    displayVariant: navItem.displayVariant,
    cta: ctaProps,
    badge: badge || undefined,
    contentPosition: navItem.contentPosition || undefined,
    items: navItem.subNavCollection?.items?.map(
      (item) => extractArgsNavigation({ ...item, __typename: 'Navigation' }), // @TODO keys.contentTypes.navigation
    ),
  });
};

// @TODO: Unit Test
export const extractArgsEmbed = (source: EmbedResponseDto) => {
  return EmbedProps.parse({
    url: source.embedSettings?.url,
  });
};

// @TODO: Unit Test
export const extractArgsContributor = (source: ContributorResponseDto) => {
  return ContributorProps.parse({
    name: source.name || undefined,
    position: source.position || undefined,
    image: source.image ? { ...source.image, alt: source.name } : undefined,
  });
};

// @TODO: Unit Test
export const extractArgsArticle = (source: ArticleResponseDto) => {
  const articleType = source.taxonomyArticleType || 'article';

  const intro = getRichText(source.intro as ContentResponseBody);
  const body = getRichText(source.body as ContentResponseBody, articleType);
  const conclusion = getRichText(source.conclusion as ContentResponseBody);

  const articlePublishDate =
    source.publishDate || source.sys.firstPublishedAt || undefined;

  const galleryProps = {
    items:
      source.primaryMediaCollection?.items?.reduce(
        (acc: FlexibleContentProps[], item: Media) => {
          const media = convertMediaResponseToMedia(item);
          // Use article publish date as uploadDate fallback for video schema
          if (media?.video && !media.video.uploadDate && articlePublishDate) {
            media.video = { ...media.video, uploadDate: articlePublishDate };
          }
          const parsedMedia = MediaProps.safeParse(media);
          return parsedMedia.success
            ? [...acc, { media: parsedMedia.data }]
            : acc;
        },
        [],
      ) || [],
  } as FlexibleContentProps;

  const contributors = source.contributorsCollection?.items?.map((item) =>
    extractArgsContributor(item),
  );

  const primaryCtaProps = (() => {
    if (source.primaryCta?.__typename === 'FlexibleContent') {
      return extractArgsContent(source.primaryCta);
    }
    return;
  })();
  const secondaryCtaProps = (() => {
    if (source.secondaryCta?.__typename === 'FlexibleContent') {
      return extractArgsContent(source.secondaryCta);
    }
    if (source.secondaryCta?.__typename === 'SacFilter') {
      return extractArgsSacFilter(source.secondaryCta);
    }
    return;
  })();
  const faqProps = source.faq
    ? {
        ...extractArgsContent(source.faq),
        items:
          source.faq.itemsCollection?.items?.map((item) => ({
            ...extractArgsContent(item),
          })) || [],
      }
    : undefined;

  let tableOfContents = [
    ...(intro?.tableOfContents || []),
    ...(body?.tableOfContents || []),
    ...(conclusion?.tableOfContents || []),
  ];
  if (source.taxonomyArticleType === keys.articleTypes.crisisUpdate) {
    tableOfContents = tableOfContents
      .filter((section) => section.id.includes('update-'))
      .map((section: { id: string; title: string; subtitle?: string }) => {
        const date = section.subtitle
          ? new Date(section.subtitle).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })
          : '';
        return {
          ...section,
          title: `${section.title} - ${date}`,
        };
      });
  }

  return ArticleProps.parse({
    contentfulEntryID: source.sys.id,
    title: source.pageTitle,
    summary: source.summary || undefined,
    publishDate: articlePublishDate,
    primaryMedia: galleryProps,
    intro: intro?.content,
    body: body?.content,
    conclusion: conclusion?.content,
    sections: tableOfContents,
    contributors,
    primaryCta: primaryCtaProps,
    secondaryCta: secondaryCtaProps,
    faq: faqProps,
    taxonomyArticleType: source.taxonomyArticleType,
    // todo: do a fallback to a real value
    taxonomyArticleTheme: source.taxonomyArticleTheme || undefined,
    taxonomyTopic: source.taxonomyTopic || undefined,
    taxonomyProgramCountry: source.taxonomyProgramCountry || undefined,
    taxonomyFunnel: source.taxonomyFunnel || undefined,
    taxonomyProgramRegion: source.taxonomyProgramRegion || undefined,
    modifiedDate: source.sys.publishedAt || undefined,
  });
};

// @TODO: Unit Tests
export const extractFundFromPage = (
  entry: PageResponseDto | PageResponseIndexDto,
): FundProps | undefined => {
  const fundContent = entry.referencedContentCollection?.items?.find(
    (item) => item.__typename === 'FlexibleContent' && item.fund,
  ) as FlexibleContentDto | undefined;

  if (!fundContent) {
    return undefined;
  }

  const fund = convertContentResponseToFund(
    fundContent.fund,
    fundContent.referencedContentCollection,
  );

  return fund;
};

// @TODO: Unit Tests
export const getBreadcrumbFromPage = (
  page: PageResponseDto,
  locale: LOCALES_CODES,
): Link[] => {
  return (
    page.pageHierarchy?.ancestors.map((a) => ({
      link: a.path,
      title: a.pageTitle[locale] || a.pageTitle.en || '',
    })) || []
  );
};

// @TODO: Move to utils
export const getPath = (path?: string | string[]) => {
  if (!path) {
    return '/';
  }

  if (Array.isArray(path)) {
    return `/${path
      .filter(Boolean)
      // @TODO: For some reason, some pages get a path segment with a string of "undefined".
      // Perhaps a bug to do with the trailing slash? Middleware?
      // I'm just nixing it here, but it would be nice to solve whatever is causing this.
      .filter((segment) => segment !== 'undefined')
      .join('/')}`;
  }
  return `/${path}`;
};

type SiteId = z.infer<typeof Sites>;

/**
 * Resolves the absolute URL prefix for a link based on the target content type
 * and the current site. Links targeting the same site are relative (no prefix).
 * Links targeting a different site get the appropriate domain prefix.
 *
 * The current site is derived from NEXT_PUBLIC_SITE_ID in the environment.
 *
 * Uses a switch on entryType so that any new page type defaults to the skippy
 * domain — only explicitly cross-site types need a case here.
 */
export const resolveAbsolutePrefix = (
  entryType: string | undefined,
): string => {
  const {
    NEXT_PUBLIC_SITE_ID,
    NEXT_PUBLIC_BASE_URL_MY,
    NEXT_PUBLIC_BASE_URL_DOTCOM,
  } = getPublicConfig();
  const currentSite: SiteId =
    (NEXT_PUBLIC_SITE_ID as SiteId) || Sites.enum.skippy;

  switch (entryType) {
    // flexibleAccountPage lives on the "my" site
    case keys.contentTypes.flexibleAccountPage: {
      if (currentSite === Sites.enum.my) {
        return ''; // same site → relative
      }
      return NEXT_PUBLIC_BASE_URL_MY || 'https://my.compassion.com';
    }

    // compex pages live on the compex site — no cross-linking needed yet
    case keys.contentTypes.compexPage: {
      return '';
    }

    // Everything else (flexiblePage, article, downloads, etc.) lives on skippy
    default: {
      if (currentSite === Sites.enum.skippy) {
        return ''; // same site → relative
      }
      return NEXT_PUBLIC_BASE_URL_DOTCOM || 'https://www.compassion.com';
    }
  }
};
/**
 * Join an absolute site origin with a page path (stripping the stray
 * `/undefined` segment) wrapped in leading and trailing slashes.
 *
 * Falls back to the absolute site origin when `siteRootUrl` is missing so any
 * JSON-LD `@id`s derived from the result stay valid absolute IRIs. In practice
 * `getSiteDefinition().siteRootUrl` is always set.
 */
export const buildCanonicalUrl = (
  path: string | undefined,
  siteRootUrl?: string | null,
): string => {
  const origin = siteRootUrl || generalConfig.domain.compassion;
  return `${origin}${slashes(path?.replace('/undefined', ''), 'both')}`;
};

/**
 * Canonical URL for a page. Single source of truth shared by
 * `getMetadataFromPage` (the canonical link tag) and the JSON-LD graph builder
 * so the two can never drift.
 */
export const getCanonicalUrl = (
  page: Pick<PageResponseDto, 'path'>,
  siteRootUrl?: string | null,
): string => buildCanonicalUrl(page.path, siteRootUrl);
